import { type FC } from "react";
import { Toolbar, Checkbox, Button, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import {
  pollErrorKey,
  useDeletePollsMutation,
  type LibraryPollsItem,
} from "@/partner/entities/polls";

interface PollsLibraryListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: LibraryPollsItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedIds: string[];
  onCloseSelected: () => void;
}

export const PollsLibraryListToolbar: FC<PollsLibraryListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedIds,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [deletePolls, { loading }] = useDeletePollsMutation();

  const onDeleteSelected = () => {
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deletePolls({
          variables: { ids: selectedIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("pollsLibrary.toolbar.deleteSuccess"), {
              variant: "success",
            });
          },
          // Прикреплённый опрос удалить нельзя: POLL_IN_USE с понятным текстом.
          onError: (error) =>
            enqueueSnackbar(t(pollErrorKey(error)), { variant: "error" }),
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{ marginLeft: "-16px", marginRight: { xs: 0, sm: "16px" } }}
      />
      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>
      <Divider
        orientation="vertical"
        variant="middle"
        sx={{ mx: 1.25, height: "2em" }}
      />
      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={loading || numSelected === 0}
      >
        {t("common.actions.delete")}
      </Button>
      <ToolbarProgress loading={loading} />
      {confirmDialog}
    </Toolbar>
  );
};
